import React, { useEffect } from "react";
import StopIcon from "@material-ui/icons/Stop";
import RestoreIcon from "@material-ui/icons/Restore";
import { useDispatch, useSelector } from "react-redux";
import { withStyles } from "@material-ui/core/styles";
import { Typography, WithStyles } from "@material-ui/core";
import { styles } from "./TasksView.styles";
import TaskModel from "../../../models/task/TaskModel";
import * as TaskAction from "../../../stores/tasks/TaskAction";
import UniversalTable from "../../components/UniversalTable/UniversalTable";
import {
    IUniversalTableColumn,
    IUniversalTableData
} from "../../components/UniversalTable/interfaces";
import { useSnackbar } from "notistack";
import ToastStatusEnum from "../../../constants/ToastStatusEnum";
import { useTranslation } from "react-i18next";
import { selectRequesting } from "../../../selectors/requesting/RequestingSelector";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { CenteredContainer } from "../../components/CenteredContainer/CenteredContainer";

export interface ITasksViewProps extends WithStyles<typeof styles> {}

const TasksViewPure: React.FunctionComponent<ITasksViewProps> = ({ classes }: ITasksViewProps) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const isRequesting: boolean = useSelector(state => selectRequesting(state, [TaskAction.REQUEST_ALL_TASKS]));

    useEffect(() => {
        dispatch(TaskAction.requestAllTasks());
    }, [dispatch]);

    const tasks: IUniversalTableData = Object.values(useSelector(state => state.tasks))?.map((task: TaskModel) => ({
        id: task.id,
        recur: task.recur,
        state: task.state,
        orderItemId: task.orderItemId,
        product: task.product,
        startTime: task.startTime,
        finishTime: task.finishTime,
        cmd: task.cmd,
        from: task.from,
        to: task.to,
        description: task.description,
        remark: task.remark,
        productFolder: task.productFolder,
        subscriptionType: task.subscriptionType,
        subscription: task.subscription,
        orderId: task.orderId,
        forceExecute: task.forceExecute,
    }));

    const handleRestartTask = index => {
        dispatch(TaskAction.restartTask(tasks[index].id))
            .then(() => {
                dispatch(TaskAction.requestAllTasks());
                enqueueSnackbar(t("admin.taskView.taskRestarted"), { variant: ToastStatusEnum.Info });
            })
            .catch(error =>
                enqueueSnackbar(t("admin.taskView.taskRestartError") + ": " + error, { variant: ToastStatusEnum.Error })
            );
    };

    const handleStopTask = index => {
        dispatch(TaskAction.stopTask(tasks[index].id))
            .then(() => {
                dispatch(TaskAction.requestAllTasks());
                enqueueSnackbar(t("admin.taskView.taskStopped"), { variant: ToastStatusEnum.Info });
            })
            .catch(error =>
                enqueueSnackbar(t("admin.taskView.taskStopError") + ": " + error, { variant: ToastStatusEnum.Error })
            );
    };

    const columns: IUniversalTableColumn[] = [
        {
            type: "text",
            title: t("admin.taskView.id"),
            field: "id"
        },
        {
            type: "text",
            title: "Recurring",
            field: "recur"
        },
        {
            type: "text",
            title: "State",
            field: "state"
        },
        {
            type: "text",
            title: "Order item id",
            field: "orderItemId"
        },
        {
            type: "text",
            title: "Product",
            field: "product"
        },
        {
            type: "dateTime",
            title: t("admin.taskView.startTime"),
            field: "startTime"
        },
        {
            type: "dateTime",
            title: t("admin.taskView.finishTime"),
            field: "finishTime"
        },
        {
            type: "text",
            title: "Command",
            field: "cmd"
        },
        {
            type: "date",
            title: "From",
            field: "from"
        },
        {
            type: "date",
            title: "To",
            field: "to"
        },
        {
            type: "text",
            title: "Description",
            field: "description"
        },
        {
            type: "text",
            title: "Remark",
            field: "remark"
        },
        {
            type: "text",
            title: "Product folder",
            field: "productFolder"
        },
        {
            type: "text",
            title: "Subscription type",
            field: "subscriptionType"
        },
        {
            type: "text",
            title: "Subscription",
            field: "subscription"
        },
        {
            type: "text",
            title: "Order id",
            field: "orderId"
        },
        {
            type: "boolean",
            title: "Force execute",
            field: "forceExecute"
        },
        {
            type: "actions",
            title: t("admin.taskView.actions"),
            actions: [
                {
                    name: "restart",
                    icon: <RestoreIcon />,
                    tooltip: t("admin.taskView.restart"),
                    onClick: index => handleRestartTask(index),
                    // scope: "tasks.write"
                },
                {
                    name: "stop",
                    icon: <StopIcon />,
                    tooltip: t("admin.taskView.stop"),
                    onClick: index => handleStopTask(index),
                    // scope: "tasks.write"
                }
            ]
        }
    ];

    // const detailPanelTableColumns: IUniversalTableDetailPanelColumn[] = [
    //     {
    //         title: t("admin.taskView.command"),
    //         field: "cmd"
    //     },
    //     {
    //         title: t("admin.taskView.isins"),
    //         field: "isins"
    //     },
    //     {
    //         title: t("admin.taskView.remark"),
    //         field: "remark"
    //     },
    //     {
    //         title: t("admin.taskView.recurrent"),
    //         field: "recur"
    //     }
    // ];

    return (
        <CenteredContainer>
            <Breadcrumbs
                items={[{ title: t("admin.adminPanel"), href: "/admin" }, { title: t("admin.taskView.tasks") }]}
            />
            <div className={classes.title}>
                <Typography variant="h3">{t("admin.taskView.tasks")}</Typography>
            </div>
            <UniversalTable
                isLoading={isRequesting}
                columns={columns}
                data={tasks}
                searchable
                // detailPanelTableColumns={detailPanelTableColumns}
            />
        </CenteredContainer>
    );
};

export const TasksView = withStyles(styles)(TasksViewPure);
